import React, { useMemo, useRef, useCallback, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  TableContainer,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  Button,
  Typography,
  Chip,
  IconButton,
  Paper,
  Menu,
  Tooltip,
} from '@mui/material';
import { 
  Search, 
  GetApp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Clear
} from '@mui/icons-material';
import LoadingTableBody from '../Table/Loading';

/**
 * Internal timer component that manages its own state
 * 
 * PERFORMANCE OPTIMIZATION:
 * This timer runs internally within the table component instead of 
 * receiving currentQueryTime as a prop from the parent Editor component.
 * 
 * Benefits:
 * - Eliminates 20 re-renders/sec of the entire Editor component
 * - Only this small timer component re-renders every 50ms
 * - Maintains smooth millisecond precision as requested
 * - Automatically pauses when browser tab is not visible
 * - Dramatically improves overall application performance
 */
const InternalTimer = React.memo(({ isRunning }) => {
  const [timerText, setTimerText] = useState('0.0s');
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  React.useEffect(() => {
    if (isRunning) {
      // Start the timer
      startTimeRef.current = performance.now();
      setTimerText('0.0s');
      
      timerRef.current = setInterval(() => {
        if (!document.hidden) { // Pause when tab not visible
          const elapsed = performance.now() - startTimeRef.current;
          const seconds = Math.floor(elapsed / 1000);
          const centiseconds = Math.floor((elapsed % 1000) / 10); // Get hundredths of a second (0-99)
          setTimerText(`${seconds}.${centiseconds.toString().padStart(2, '0')}s`);
        }
      }, 50); // Keep 50ms for smooth millisecond updates
      
    } else {
      // Stop the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      startTimeRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRunning]);

  if (!isRunning) return null;

  return (
    <Typography variant="caption" sx={{ color: '#666' }}>
      {timerText}
    </Typography>
  );
});

InternalTimer.displayName = 'InternalTimer';

/**
 * Enhanced SQL Results Table with enterprise features
 * - Global search across all columns
 * - Per-column filtering
 * - Column show/hide
 * - Export to CSV
 * - Virtualization for performance
 * - Internal timer for loading state (eliminates parent re-renders)
 * - Compatible with React 17
 * 
 * PERFORMANCE NOTE:
 * The timer now runs internally instead of receiving currentQueryTime prop.
 * This prevents the entire Editor component from re-rendering every 50ms,
 * while maintaining smooth millisecond precision for the timer display.
 */
const EnhancedSQLResultsTable = ({
  data = [],
  columns = [],
  isLoading = false,
  isRetrievingFromStorage = false,
  onSortColumn = () => {},
  sortedColumn = { column: "", isAsc: true },
  queryInfo = null, // { fileName, startedAt, queryTime, rowCount }
  error = null, // { message, variant, showFixButton, onFixClick }
}) => {
  // Debug logging for localStorage restoration
  React.useEffect(() => {
  }, [data, columns, isLoading, isRetrievingFromStorage, queryInfo]);
  const parentRef = useRef();
  const topScrollRef = useRef();
  const [searchTerm, setSearchTerm] = useState('');
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [columnControlsAnchor, setColumnControlsAnchor] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  
  // Column resizing state
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizeColumnIndex, setResizeColumnIndex] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Process columns to include row numbers
  const processedColumns = useMemo(() => {
    // Filter out empty, null, undefined, or whitespace-only column names
    const validColumns = columns.filter(col => col && String(col).trim());
    const cols = ['#', ...validColumns];
    return cols.filter(col => !hiddenColumns.has(col));
  }, [columns, hiddenColumns]);

  // Enhanced filtering with global search and column filters
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    let filtered = [...data];
    
    // Apply global search across all columns
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(row => 
        Object.values(row).some(value => 
          String(value || '').toLowerCase().includes(searchLower)
        )
      );
    }
    
    // Apply individual column filters
    Object.entries(columnFilters).forEach(([column, filterValue]) => {
      if (filterValue && filterValue.trim()) {
        const filterLower = filterValue.toLowerCase();
        filtered = filtered.filter(row => 
          String(row[column] || '').toLowerCase().includes(filterLower)
        );
      }
    });
    
    return filtered;
  }, [data, searchTerm, columnFilters]);

  // Dynamic column width calculation with resize support
  const getColumnWidth = useCallback((index) => {
    const columnName = processedColumns[index];
    
    // Use stored width if available
    if (columnWidths[columnName] !== undefined) {
      return Math.max(60, columnWidths[columnName]); // Minimum 60px
    }
    
    // Default widths
    if (index === 0) return 80; // Row number column
    
    // Calculate width based on column name and content
    const nameLength = columnName?.length || 0;
    const minWidth = 120;
    const maxWidth = 350;
    const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, nameLength * 12 + 80));
    
    return calculatedWidth;
  }, [processedColumns, columnWidths]);

  // Process data for virtualization
  const processedData = useMemo(() => {
    return filteredData.map((row, index) => ({
      id: index,
      originalIndex: data.findIndex(dataRow => dataRow === row),
      rowNumber: index + 1,
      data: row,
      columns: processedColumns.map((column, colIndex) => {
        if (colIndex === 0) return index + 1; // Row number
        return row[column] || '';
      })
    }));
  }, [filteredData, processedColumns, data]);

  // Virtualizers for performance
  const rowVirtualizer = useVirtualizer({
    count: processedData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40, // Row height
    overscan: 10, // Render extra rows for smooth scrolling
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: processedColumns.length,
    getScrollElement: () => parentRef.current,
    estimateSize: getColumnWidth,
    overscan: 3,
  });

  // Force virtualizer re-render when column widths change
  React.useEffect(() => {
    columnVirtualizer.measure();
  }, [columnWidths, columnVirtualizer]);

  // Enhanced cell rendering with formatting
  const renderTableCell = useCallback((value, columnName) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? '✓' : '✗';
    if (typeof value === 'number') {
      // Format numbers nicely
      if (columnName?.toLowerCase().includes('id')) {
        return value.toString();
      }
      if (Number.isInteger(value)) {
        return value.toLocaleString();
      }
      return Number(value).toLocaleString(undefined, { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      });
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '[Object]';
      }
    }
    
    const strValue = String(value);
    // Truncate very long strings
    if (strValue.length > 100) {
      return strValue.substring(0, 97) + '...';
    }
    
    return strValue;
  }, []);

  // Event handlers
  const handleColumnFilter = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const handleColumnToggle = (column) => {
    const newHidden = new Set(hiddenColumns);
    if (newHidden.has(column)) {
      newHidden.delete(column);
    } else {
      newHidden.add(column);
    }
    setHiddenColumns(newHidden);
  };

  const handleSort = (columnName) => {
    if (columnName !== '#') {
      onSortColumn(columnName);
    }
  };

  const handleExport = () => {
    if (filteredData.length === 0) return;
    
    const exportColumns = processedColumns.filter(col => col !== '#');
    const csvContent = [
      exportColumns.join(','),
      ...filteredData.map(row => 
        exportColumns
          .map(col => {
            const value = row[col] || '';
            // Escape CSV values
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
          })
          .join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sql-results-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setColumnFilters({});
  };

  // Column resize handlers
  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const columnName = processedColumns[columnIndex];
    const currentWidth = getColumnWidth(columnIndex);
    
    setIsResizing(true);
    setResizeColumnIndex(columnIndex);
    setResizeStartX(e.clientX);
    setResizeStartWidth(currentWidth);
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    // Add a temporary visual indicator
    document.body.classList.add('resizing-column');
  };

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || resizeColumnIndex === null) return;
    
    const deltaX = e.clientX - resizeStartX;
    const newWidth = Math.max(60, resizeStartWidth + deltaX); // Minimum 60px
    const columnName = processedColumns[resizeColumnIndex];
    
    setColumnWidths(prev => ({
      ...prev,
      [columnName]: newWidth
    }));
  }, [isResizing, resizeColumnIndex, resizeStartX, resizeStartWidth, processedColumns]);

  const handleResizeEnd = useCallback(() => {
    if (!isResizing) return;
    
    setIsResizing(false);
    setResizeColumnIndex(null);
    setResizeStartX(0);
    setResizeStartWidth(0);
    
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.classList.remove('resizing-column');
  }, [isResizing]);

  // Attach global mouse events for resizing
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const activeFiltersCount = Object.values(columnFilters).filter(v => v?.trim()).length + (searchTerm.trim() ? 1 : 0);

  // Calculate if we need to stretch columns to fill container width
  const totalColumnWidth = columnVirtualizer.getTotalSize();
  const containerWidth = parentRef.current?.clientWidth || 0;
  const needsStretching = totalColumnWidth < containerWidth;
  const stretchFactor = needsStretching ? containerWidth / totalColumnWidth : 1;

  // Loading state - special case for retrieving from storage
  if (isRetrievingFromStorage) {
    return (
      <Box sx={{ 
        height: '100%',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e0e0e0',
        borderRadius: 0,
        overflow: 'hidden',
        margin: 0,
      }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: '3px solid #e0e0e0',
            borderTop: '3px solid #2196f3',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      </Box>
    );
  }
  
  // Loading state for regular query execution
  if (isLoading) {
    return (
      <Box sx={{ height: '100%', border: '1px solid #e0e0e0', borderRadius: 0, display: 'flex', flexDirection: 'column' }}>
        <Paper elevation={0} sx={{ p: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa', borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* File Name */}
            {queryInfo && (
              <Chip
                label={queryInfo.fileName}
                variant="outlined"
                sx={{
                  height: 33, // Match button height
                  borderColor: '#448593',
                  backgroundColor: 'white',
                  color: '#448593',
                  fontWeight: 500,
                  fontSize: '10px',
                  borderRadius: '4px',
                  px: 1,
                  '& .MuiChip-label': {
                    fontSize: '11px',
                    fontWeight: 500,
                    px: 1.5,
                  },
                  '&:hover': {
                    borderColor: '#3a7380',
                    backgroundColor: '#f8fafb',
                  },
                }}
              />
            )}
            
            <TextField
              size="small"
              placeholder="Search..."
              disabled
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <InternalTimer isRunning={isLoading} />
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid #e0e0e0',
                  borderTop: '2px solid #2196f3',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
        <TableContainer sx={{ flex: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: '#fafafa',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: '#333',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      height: '12px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '6px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  />
                </TableCell>
                {columns.length > 0 ? (
                  // Show actual column names if available
                  columns.map((column) => (
                                         <TableCell 
                       key={column}
                       sx={{ 
                         backgroundColor: '#fafafa',
                         borderBottom: '2px solid #e0e0e0',
                         fontWeight: 600,
                         fontSize: '11px',
                         color: '#333',
                       }}
                    >
                      {column}
                    </TableCell>
                  ))
                ) : (
                  // Show empty headers with loading animation if no columns yet
                  Array.from({ length: 5 }, (_, index) => (
                                         <TableCell 
                       key={index}
                       sx={{ 
                         backgroundColor: '#fafafa',
                         borderBottom: '2px solid #e0e0e0',
                         fontWeight: 600,
                         fontSize: '11px',
                         color: '#333',
                         position: 'relative',
                       }}
                    >
                      <Box
                        sx={{
                          height: '12px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '6px',
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                            '100%': { opacity: 1 },
                          },
                        }}
                      />
                    </TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <LoadingTableBody colNum={columns.length > 0 ? columns.length + 1 : 6} rowCount={10} />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  // Error state - show error where table would be
  if (error && error.message) {
    return (
      <Box sx={{ 
        height: '100%',
        display: 'flex', 
        flexDirection: 'column', 
        border: '1px solid #e0e0e0',
        borderRadius: 0,
        overflow: 'hidden',
        margin: 0,
      }}>
        {/* Enhanced Toolbar */}
        <Paper elevation={0} sx={{ p: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa', borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* File Name */}
            {queryInfo && (
              <Chip
                label={queryInfo.fileName.length > 30 ? `${queryInfo.fileName.substring(0, 27)}...` : queryInfo.fileName}
                variant="outlined"
                title={queryInfo.fileName}
                sx={{
                  height: 33,
                  borderColor: '#448593',
                  backgroundColor: 'white',
                  color: '#448593',
                  fontWeight: 500,
                  fontSize: '10px',
                  borderRadius: '4px',
                  px: 1,
                  '& .MuiChip-label': {
                    fontSize: '11px',
                    fontWeight: 500,
                    px: 1.5,
                  },
                  '&:hover': {
                    borderColor: '#3a7380',
                    backgroundColor: '#f8fafb',
                  },
                }}
              />
            )}
            
            {/* Disabled search for error state */}
            <TextField
              size="small"
              placeholder="Search..."
              disabled
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {queryInfo && queryInfo.queryTime && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Started at: {queryInfo.startedAt} | {queryInfo.queryTime}s
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

                          {/* Error Display */}
         <Box sx={{ 
           flex: 1, 
           display: 'flex', 
           flexDirection: 'column',
           alignItems: 'center',
           justifyContent: 'flex-start',
           padding: 3,
           backgroundColor: error.variant === 'danger' ? '#fef2f2' : '#f8f9fa',
           border: `2px solid ${error.variant === 'danger' ? '#fecaca' : '#e5e7eb'}`,
           borderRadius: 1,
           margin: 2,
           overflow: 'auto',
           minHeight: 0
         }}>
           <Box sx={{ 
             display: 'flex', 
             alignItems: 'flex-start', 
             gap: 2,
             maxWidth: '800px',
             width: '100%',
             marginTop: 'auto',
             marginBottom: 'auto'
           }}>
            {/* Error Icon */}
            <Box sx={{ 
              minWidth: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: error.variant === 'danger' ? '#fee2e2' : '#f3f4f6',
              color: error.variant === 'danger' ? '#dc2626' : '#6b7280'
            }}>
              {error.variant === 'danger' ? (
                <Box sx={{ fontSize: '20px' }}>⚠️</Box>
              ) : (
                <Box sx={{ fontSize: '20px' }}>ℹ️</Box>
              )}
            </Box>
            
            {/* Error Content */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '11px', 
                  fontWeight: 600, 
                  color: error.variant === 'danger' ? '#dc2626' : '#374151',
                  mb: 1
                }}
              >
                {error.variant === 'danger' ? 'Query Error' : 'Query Info'}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '11px',
                  color: error.variant === 'danger' ? '#7f1d1d' : '#6b7280',
                  lineHeight: 1.5,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
                dangerouslySetInnerHTML={{ __html: error.message }}
              />
            </Box>
            
            {/* Fix Button */}
            {error.showFixButton && !error.message.includes("There was an error communicating with the backend") && (
              <Box>
                <Button
                  variant="contained"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 3,
                    py: 1,
                    backgroundColor: '#dc2626',
                    '&:hover': {
                      backgroundColor: '#b91c1c',
                    },
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                  }}
                  onClick={error.onFixClick}
                >
                  Fix now
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Handle empty results case - show table with no results message
  const hasNoResults = (!data || data.length === 0) && !isLoading && !isRetrievingFromStorage && !error;

  if (hasNoResults) {
    return (
      <Box sx={{ 
        height: '100%',
        display: 'flex', 
        flexDirection: 'column', 
        border: '1px solid #e0e0e0',
        borderRadius: 0,
        overflow: 'hidden',
        margin: 0,
      }}>
        {/* Enhanced Toolbar */}
        <Paper elevation={0} sx={{ p: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa', borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* File Name */}
            {queryInfo && (
              <Chip
                label={queryInfo.fileName.length > 30 ? `${queryInfo.fileName.substring(0, 27)}...` : queryInfo.fileName}
                variant="outlined"
                title={queryInfo.fileName}
                sx={{
                  height: 33,
                  borderColor: '#448593',
                  backgroundColor: 'white',
                  color: '#448593',
                  fontWeight: 500,
                  fontSize: '10px',
                  borderRadius: '4px',
                  px: 1,
                  '& .MuiChip-label': {
                    fontSize: '11px',
                    fontWeight: 500,
                    px: 1.5,
                  },
                  '&:hover': {
                    borderColor: '#3a7380',
                    backgroundColor: '#f8fafb',
                  },
                }}
              />
            )}
            
            {/* Global Search - disabled for no results */}
            <TextField
              size="small"
              placeholder="Search all columns..."
              disabled
              sx={{ minWidth: 200, borderRadius: '4px' }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />

            {/* Column Controls - disabled */}
            <Button
              size="small"
              variant="outlined"
              disabled
              sx={{
                fontSize: "10px",
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '4px',
                height: 33,
                minWidth: 120,
                maxWidth: 160,
                px: 2,
                py: 1,
                borderColor: '#e0e0e0',
                color: '#666',
                backgroundColor: 'white',
              }}
              endIcon={<KeyboardArrowDown />}
            >
              Columns (0)
            </Button>

            {/* Stats */}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
              {queryInfo && queryInfo.queryTime && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Started at: {queryInfo.startedAt} | {queryInfo.queryTime}s
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Table with no results message */}
        <TableContainer sx={{ flex: 1 }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell 
                  sx={{ 
                    textAlign: 'center',
                    py: 4,
                    color: '#666',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    border: 'none'
                  }}
                >
                  The query returned no results
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  // If no data and not loading, don't render anything (e.g., storage retrieval failed)
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column', 
      border: '1px solid #e0e0e0',
      borderRadius: 0,
      overflow: 'hidden',
      margin: 0,
    }}>
      {/* Enhanced Toolbar */}
      <Paper elevation={0} sx={{ p: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa', borderRadius: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* File Name */}
          {queryInfo && (
            <Chip
              label={queryInfo.fileName.length > 30 ? `${queryInfo.fileName.substring(0, 27)}...` : queryInfo.fileName}
              variant="outlined"
              title={queryInfo.fileName} // Show full filename on hover
              sx={{
                height: 33, // Match button height
                borderColor: '#448593',
                backgroundColor: 'white',
                color: '#448593',
                fontWeight: 500,
                fontSize: '10px',
                borderRadius: '4px',
                px: 1,
                '& .MuiChip-label': {
                  fontSize: '11px',
                  fontWeight: 500,
                  px: 1.5,
                },
                '&:hover': {
                  borderColor: '#3a7380',
                  backgroundColor: '#f8fafb',
                },
              }}
            />
          )}
          
          {/* Global Search */}
          <TextField
            size="small"
            placeholder="Search all columns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200, borderRadius: '4px' }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              endAdornment: searchTerm && (
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                >
                  <Clear fontSize="small" />
                </IconButton>
              )
            }}
          />

          {/* Column Controls */}
          <Box sx={{ position: 'relative' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setColumnControlsAnchor(e.currentTarget)}
              sx={{
                fontSize: "10px",
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '4px',
                height: 33,
                minWidth: 120,
                maxWidth: 160,
                px: 2,
                py: 1,
                borderColor: '#e0e0e0',
                color: '#666',
                backgroundColor: 'white',
                '&:hover': {
                  borderColor: '#d0d0d0',
                  backgroundColor: '#f8f9fa',
                },
              }}
              endIcon={<KeyboardArrowDown />}
            >
              {isLoading ? "Columns" : `Columns (${processedColumns.filter(col => col !== '#').length}/${columns.filter(col => col && String(col).trim()).length})`}
            </Button>
            <Menu
              anchorEl={columnControlsAnchor}
              open={Boolean(columnControlsAnchor)}
              onClose={(event, reason) => {
                // Don't close on item selection, only on outside clicks or escape
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                  setColumnControlsAnchor(null);
                }
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                style: {
                  maxHeight: 400,
                  marginTop: 4,
                  padding: 0,
                  overflow: 'hidden',
                  minWidth: columnControlsAnchor?.offsetWidth || 160,
                },
                sx: {
                  '& .MuiList-root': {
                    padding: 0,
                    overflow: 'auto',
                    maxHeight: 400,
                  }
                }
              }}
              disablePortal={false}
              disableScrollLock={true}
            >
              {['#', ...columns]
                .filter(column => column && String(column).trim() && column !== '#')
                .map((column) => (
                <MenuItem 
                  key={column} 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleColumnToggle(column);
                  }}
                  sx={{ 
                    fontSize: "10px", 
                    fontWeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                    minWidth: 160,
                  }}
                >
                  <Checkbox
                    checked={!hiddenColumns.has(column)}
                    disabled={column === '#'} // Don't allow hiding row numbers
                    size="small"
                    onChange={(e) => {
                      e.stopPropagation();
                      handleColumnToggle(column);
                    }}
                    sx={{
                      p: 0,
                      color: '#2196f3',
                      '&.Mui-checked': {
                        color: '#2196f3',
                      },
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: column === '#' ? '#666' : '#333',
                      fontSize: '10px',
                    }}
                  >
                    {column}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={clearAllFilters}
              sx={{ color: 'warning.main' }}
            >
              Clear All Filters
            </Button>
          )}

          {/* Stats */}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
            
            {/* Query execution info with row count */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {queryInfo && queryInfo.queryTime && (
                <>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Started at: {queryInfo.startedAt} | {queryInfo.queryTime}s | 
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: queryInfo?.queryTime ? 0.5 : 0 }}>
                {filteredData.length === data.length 
                  ? `${data.length.toLocaleString()} rows` 
                  : `${filteredData.length.toLocaleString()} of ${data.length.toLocaleString()} rows`
                }
              </Typography>
              
              {/* Export CSV button - only icon */}
              <Tooltip
                title="Export CSV"
                placement="bottom"
                arrow
                disableInteractive={true}
                slotProps={{
                  arrow: {
                    sx: {
                      color: "#546e7a"
                    }
                  },
                  tooltip: {
                    sx: {
                      backgroundColor: "#546e7a",
                      fontSize: "11px",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                    }
                  }
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleExport}
                  disabled={filteredData.length === 0}
                  sx={{
                    ml: 1,
                    color: filteredData.length === 0 ? '#999' : '#666',
                    '&:hover': {
                      backgroundColor: filteredData.length === 0 ? 'transparent' : '#f8f9fa',
                    },
                    '&.Mui-disabled': {
                      color: '#999',
                    },
                  }}
                >
                  <GetApp fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Active Filters Display */}
      {Object.entries(columnFilters).some(([, value]) => value?.trim()) && (
        <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ mr: 1, fontWeight: 500 }}>Column Filters:</Typography>
            {Object.entries(columnFilters)
              .filter(([, value]) => value?.trim())
              .map(([column, value]) => (
                <Chip
                  key={column}
                  label={`${column}: "${value}"`}
                  size="small"
                  onDelete={() => handleColumnFilter(column, '')}
                  variant="outlined"
                  color="primary"
                />
              ))}
          </Box>
        </Box>
      )}

      {/* Top Horizontal Scrollbar */}
      <Box
        ref={topScrollRef}
        sx={{
          overflowX: 'scroll',
          overflowY: 'hidden',
          height: '12px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            height: 12,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 6,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
            '&:hover': {
              backgroundColor: '#a1a1a1',
            },
          },
        }}
        onScroll={(e) => {
          if (parentRef.current) {
            parentRef.current.scrollLeft = e.target.scrollLeft;
          }
        }}
      >
        <Box sx={{ width: `max(100%, ${columnVirtualizer.getTotalSize()}px)`, height: '1px' }} />
      </Box>

      {/* Virtualized Table */}
      <TableContainer
        ref={parentRef}
        onScroll={(e) => {
          if (topScrollRef.current) {
            topScrollRef.current.scrollLeft = e.target.scrollLeft;
          }
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: 'scroll',
          overflowY: 'auto',
          // Hide the bottom horizontal scrollbar
          '&::-webkit-scrollbar:horizontal': {
            display: 'none',
          },
          '& .MuiTableCell-head': {
            backgroundColor: '#fafafa !important',
            borderBottom: '2px solid #e0e0e0',
            fontWeight: 600,
            fontSize: '11px',
            color: '#333',
          },
          '& .MuiTableCell-body': {
            fontSize: '10px',
            borderBottom: '1px solid #f0f0f0',
            padding: '6px 12px',
          },
          '& .MuiTable-root': {
            width: `max(100%, ${columnVirtualizer.getTotalSize()}px)`,
          },
          '& .MuiTableRow-root': {
            height: '40px !important',
            '&:hover': {
              backgroundColor: '#f8f9fa !important',
            },
          },
          // Custom scrollbar - always visible
          '&::-webkit-scrollbar': {
            width: 12,
            height: 12,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 6,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
            '&:hover': {
              backgroundColor: '#a1a1a1',
            },
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: '#f1f1f1',
          },
        }}
      >
        <Table 
          sx={{ 
            position: 'relative', 
            width: `max(100%, ${columnVirtualizer.getTotalSize()}px)`,
            // Global style for resize handles
            '& [data-resize-handle]': {
              '&::after': {
                content: '""',
                position: 'absolute',
                right: '2px',
                top: '25%',
                height: '50%',
                width: '2px',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '1px',
                opacity: 0,
                transition: 'opacity 0.2s',
              },
              '&:hover::after': {
                opacity: 1,
              },
            },
          }}
        >
          {/* Sticky Header */}
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              height: '35px',
              backgroundColor: '#fafafa',
            }}
          >
            <TableRow sx={{ height: '35px' }}>
              {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                const columnName = processedColumns[virtualColumn.index];
                const isSorted = sortedColumn.column === columnName;
                const isRowNumber = columnName === '#';
                const hasFilter = columnFilters[columnName]?.trim();
                
                return (
                  <TableCell
                    key={virtualColumn.key}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      transform: `translateX(${virtualColumn.start * stretchFactor}px)`,
                      width: virtualColumn.size * stretchFactor,
                      height: '35px',
                      cursor: isRowNumber ? 'default' : 'pointer',
                      userSelect: 'none',
                      backgroundColor: hasFilter ? '#e3f2fd' : '#fafafa',
                      '&:hover': {
                        backgroundColor: isRowNumber ? '#fafafa' : '#f0f0f0',
                      },
                      borderRight: '1px solid #e0e0e0',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: 600,
                      fontSize: '11px',
                      color: '#448593',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={(e) => {
                      // Don't sort if clicking on resize handle
                      if (e.target.closest('[data-resize-handle]')) {
                        return;
                      }
                      handleSort(columnName);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', position: 'relative' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '11px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#448593'
                          }}
                        >
                          {isRowNumber ? '' : columnName}
                        </Typography>
                        {isSorted && (
                          <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                            {sortedColumn.isAsc ? (
                              <KeyboardArrowUp fontSize="small" color="primary" />
                            ) : (
                              <KeyboardArrowDown fontSize="small" color="primary" />
                            )}
                          </Box>
                        )}
                      </Box>
                      
                      {/* Column Resize Handle */}
                      <Box
                        data-resize-handle
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleResizeStart(e, virtualColumn.index);
                        }}
                        sx={{
                          position: 'absolute',
                          right: -20,
                          top: -10,
                          width: 50,
                          height: 'calc(100% + 20px)',
                          cursor: 'col-resize',
                          backgroundColor: 'transparent',
                          zIndex: 1000
                        }}
                      />
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          {/* Virtualized Body */}
          <TableBody>
            <div style={{ 
              height: rowVirtualizer.getTotalSize(), 
              width: '100%', 
              position: 'relative'
            }}>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const rowData = processedData[virtualRow.index];
                if (!rowData) return null;

                return (
                  <TableRow
                    key={virtualRow.key}
                    hover
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                      height: virtualRow.size,
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa',
                      },
                    }}
                  >
                    {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                      const cellContent = rowData.columns[virtualColumn.index];
                      const columnName = processedColumns[virtualColumn.index];
                      const isRowNumber = columnName === '#';

                      return (
                        <TableCell
                          key={virtualColumn.key}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            transform: `translateX(${virtualColumn.start * stretchFactor}px)`,
                            width: virtualColumn.size * stretchFactor,
                            borderWidth: 0,
                            height: virtualRow.size,
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: isRowNumber ? 500 : 'normal',
                            color: isRowNumber ? 'text.secondary' : 'text.primary',
                            backgroundColor: 'inherit',
                            borderRight: isRowNumber ? '1px solid #e0e0e0' : 'none',
                          }}
                          title={String(cellContent)}
                        >
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {renderTableCell(cellContent, columnName)}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </div>
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
};

export default EnhancedSQLResultsTable;
